import { QuantityChangeAction } from "@/app/(site)/[locale]/checkout/types"
import { IconBin, IconMinus, IconPlus } from "@/app/(site)/_components/Atoms/Icons"
import IconTagIcon from "@/app/(site)/_components/Atoms/Icons/IconTagIcon"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { type CheckoutStep } from "@/app/(site)/contexts/ecommerce/types"
import { type ProductData } from "@/lib/content-service/types/ContentServiceProduct"
import { bunnyLoader } from "@/lib/sanityLoader"
import { removeTextFromSize } from "@/lib/utils"
import { FOCUS_VISIBLE_CLASS } from "@/src/utils/a11y/focusVisible"
import { clsx } from "@/src/utils/clsx"
import { t } from "@/translations"
import placeholder from "@assets/images/placeholder.png"
import Image from "next/image"

interface BasketProductCardProps {
    product: Pick<
        ProductData,
        "name" | "showAsOnSale" | "media" | "uri" | "color_swatch" | "lens"
    >
    totalPriceBeforeDiscount: string
    totalPriceAfterDiscount: string
    hasDiscount: boolean
    lineId: string
    itemId: string
    quantity: number
    size: string
    langCode: string
    isQuantityLoading: boolean
    isVoucherLoading: boolean
    pfasRestricted: boolean
    isFinalSale?: boolean
    finalSaleMessage?: string
    handleQuantityChange: (lineId: string, action: QuantityChangeAction) => void
    containerClassName?: string
    oneProduct?: boolean
    hideBasketFunctionality?: boolean
    suffix?: () => React.ReactNode
    isLast?: boolean
    inseta11y?: { before: string }
    checkoutCurrentStep?: CheckoutStep
}

export default function BasketProductCard({
    product,
    totalPriceBeforeDiscount,
    totalPriceAfterDiscount,
    hasDiscount,
    lineId,
    itemId,
    quantity,
    size,
    langCode,
    isQuantityLoading,
    isVoucherLoading,
    pfasRestricted,
    isFinalSale = false,
    finalSaleMessage = "",
    handleQuantityChange,
    containerClassName,
    oneProduct,
    hideBasketFunctionality = false,
    suffix,
    isLast = false,
    checkoutCurrentStep = 1,
    inseta11y = {
        before: "before:absolute before:inset-0 inset-0 before:-inset-x-4 before:-bottom-4 before:-top-4 before:content-['']" //"before:absolute before:inset-0 inset-0 before:-inset-x-12 before:-bottom-12 before:-top-16 before:content-['']" Helps with a11y button press areas
    }
}: BasketProductCardProps) {
    const productUri = `/product/${product.uri}`
    const isLoading = isQuantityLoading || isVoucherLoading
    const disabledDecreaseQuantity = isLoading || quantity <= 1 || hideBasketFunctionality
    const disabledIncreaseQuantity = isLoading || hideBasketFunctionality
    const disabledPfas = isLoading || pfasRestricted
    const showFinalSaleNotice = isFinalSale && finalSaleMessage !== ""
    const productImage = (product.media as any)?.standard[0] ?? placeholder.src
    return (
        <div className={`flex flex-col gap-12 ${containerClassName}`}>
            {(pfasRestricted || showFinalSaleNotice) && (
                <div className="flex flex-col gap-8">
                    {pfasRestricted && (
                        <div className="text-col-text-alert Trade_Gothic_Text_12_150_0_300">
                            {t(langCode, "checkout.pfas.restricted_product_message_cart")}
                        </div>
                    )}
                    {showFinalSaleNotice && (
                        <div className="text-col-text-alert Trade_Gothic_Text_12_150_0_300">
                            {finalSaleMessage}
                        </div>
                    )}
                </div>
            )}

            <div
                className={clsx(
                    `grid min-h-[150px] grid-cols-[79px_1fr] gap-x-12 pb-24`,
                    !oneProduct && !isLast && "border-b border-b-main-medium-grey"
                )}
            >
                <DynamicLink href={productUri} prefetch={false}>
                    <div
                        className={clsx(
                            "relative aspect-[0.7] h-full w-full bg-col-bg-calm",
                            disabledPfas && "opacity-60"
                        )}
                    >
                        <Image
                            src={bunnyLoader({
                                src: productImage,
                                width: 100,
                                quality: 80
                            })}
                            alt={product.name}
                            fill
                            unoptimized
                            className="h-full w-full object-contain"
                        />
                    </div>
                </DynamicLink>
                <div className="flex flex-col justify-between">
                    <div className="flex flex-col gap-8">
                        <div
                            className={clsx(
                                "flex items-start justify-between gap-x-8",
                                isLoading && "opacity-60"
                            )}
                        >
                            <DynamicLink href={productUri} prefetch={false}>
                                <h4 className="Trim_Mono_POC_Text_14_130_0_500_uppercase">
                                    {product.name}
                                </h4>
                            </DynamicLink>
                            {!hideBasketFunctionality && (
                                <button
                                    type="button"
                                    className={clsx(
                                        "flex h-24 w-24 items-center justify-center",
                                        FOCUS_VISIBLE_CLASS
                                    )}
                                    onClick={() =>
                                        checkoutCurrentStep >= 1 &&
                                        !isLoading &&
                                        handleQuantityChange(
                                            lineId,
                                            QuantityChangeAction.delete
                                        )
                                    }
                                    aria-label={t(langCode, "checkout.remove_item", {
                                        productName: product.name
                                    })}
                                    disabled={isLoading}
                                >
                                    <span className="sr-only">
                                        {t(langCode, "checkout.remove_item_sr_only")}
                                    </span>
                                    <IconBin className="h-16 w-16" aria-hidden="true" />
                                </button>
                            )}
                        </div>

                        <DynamicLink href={productUri} prefetch={false}>
                            <div
                                className={clsx(
                                    "flex flex-col gap-2 pr-16 lg:pr-8",
                                    disabledPfas && "opacity-60"
                                )}
                            >
                                <p className="flex items-baseline gap-x-4">
                                    <span className="Trim_Mono_POC_Text_10_130_-3_400_uppercase lg:Trim_Mono_POC_Text_12_130_-3_400_uppercase">
                                        {product.lens
                                            ? t(langCode, "product_page.frame") + ":"
                                            : t(langCode, "product_page.color") + ":"}
                                    </span>
                                    <span className="Trade_Gothic_Text_10_150_0_300 lg:Trade_Gothic_Text_12_150_0_300">
                                        {product.color_swatch?.color_name}
                                    </span>
                                </p>
                                {product.lens && (
                                    <p className="flex items-baseline gap-x-4">
                                        <span className="Trim_Mono_POC_Text_10_130_-3_400_uppercase lg:Trim_Mono_POC_Text_12_130_-3_400_uppercase">
                                            {t(langCode, "product_page.lens") + ":"}
                                        </span>
                                        <span className="Trade_Gothic_Text_10_150_0_300 lg:Trade_Gothic_Text_12_150_0_300">
                                            {product.lens.lens_name}
                                        </span>
                                    </p>
                                )}
                                {size && (
                                    <p className="flex items-baseline gap-x-4">
                                        <span className="Trim_Mono_POC_Text_10_130_-3_400_uppercase lg:Trim_Mono_POC_Text_12_130_-3_400_uppercase">
                                            {t(langCode, "product_page.size") + ":"}
                                        </span>
                                        {size.toLowerCase() === "one size" ||
                                        size.toLowerCase() === "regular fit" ? (
                                            <span className="capitalize Trade_Gothic_Text_10_150_0_300 lg:Trade_Gothic_Text_12_150_0_300">
                                                {size}
                                            </span>
                                        ) : (
                                            <span className="Trade_Gothic_Text_10_150_0_300 lg:Trade_Gothic_Text_12_150_0_300">
                                                {removeTextFromSize(size)}
                                            </span>
                                        )}
                                    </p>
                                )}
                                {suffix && suffix()}
                            </div>
                        </DynamicLink>
                    </div>

                    <div
                        className={clsx(
                            "flex h-24 items-center justify-between",
                            disabledPfas && "opacity-60"
                        )}
                    >
                        <div className="flex flex-wrap items-center gap-x-4 Trim_Mono_POC_Text_12_130_-3_400_uppercase sm:Trim_Mono_POC_Text_14_130_-5_400_uppercase">
                            {(product.showAsOnSale || hasDiscount) && (
                                <p className="text-secondary-text line-through">
                                    {totalPriceBeforeDiscount}
                                </p>
                            )}
                            <p className="">{totalPriceAfterDiscount}</p>
                            {hasDiscount && <IconTagIcon className="h-12 w-12" />}
                        </div>
                        <div className="flex items-center gap-x-4 Trim_Mono_POC_Text_12_130_-3_400_uppercase">
                            <button
                                type="button"
                                className={clsx(
                                    disabledDecreaseQuantity &&
                                        "disabled:cursor-not-allowed disabled:opacity-20",
                                    "relative p-4",
                                    FOCUS_VISIBLE_CLASS,
                                    inseta11y.before
                                )}
                                disabled={disabledDecreaseQuantity}
                                onClick={() =>
                                    !disabledDecreaseQuantity &&
                                    handleQuantityChange(
                                        lineId,
                                        QuantityChangeAction.decrease
                                    )
                                }
                                aria-label={t(langCode, "checkout.decrease_quantity", {
                                    productName: product.name
                                })}
                            >
                                <span className="sr-only">
                                    {t(langCode, "checkout.decrease_quantity_sr_only")}
                                </span>
                                <IconMinus className="h-16 w-16" />
                            </button>
                            {quantity}
                            <button
                                type="button"
                                className={clsx(
                                    disabledIncreaseQuantity &&
                                        "disabled:cursor-not-allowed disabled:opacity-20",
                                    "relative p-4",
                                    FOCUS_VISIBLE_CLASS,
                                    inseta11y.before
                                )}
                                disabled={disabledIncreaseQuantity}
                                onClick={() =>
                                    !disabledIncreaseQuantity &&
                                    handleQuantityChange(
                                        itemId,
                                        QuantityChangeAction.increase
                                    )
                                }
                                aria-label={t(langCode, "checkout.increase_quantity", {
                                    productName: product.name
                                })}
                            >
                                <span className="sr-only">
                                    {t(langCode, "checkout.increase_quantity_sr_only")}
                                </span>
                                <IconPlus className="h-16 w-16" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
